import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { SupplyDelivery } from 'fhir/r4b';
import { StateService } from '../shared/state.service';
import { take } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { ErrorService } from '../shared/error.service';
import { AuthService } from '../shared/auth/auth.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

type DialogData = { resource: SupplyDelivery };

@Component({
  selector: 'app-supply-stock-update-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
  ],
  template: `
    <h2 mat-dialog-title>Update Remaining Stock</h2>

    <div mat-dialog-content>
      <div style="margin-bottom: 8px;">
        <strong>{{ substanceName }}</strong>
      </div>

      <form [formGroup]="form" style="display:flex; flex-direction:column; gap:12px;">
        <mat-form-field appearance="outline">
          <mat-label>Quantity Remaining (dispensable unit)</mat-label>
          <input matInput type="number" formControlName="quantityRemaining" />
          @if (unitLabel) {<mat-hint>Unit: {{ unitLabel }}</mat-hint>}
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Packages Remaining</mat-label>
          <input matInput type="number" formControlName="packagesRemaining" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Low-stock Threshold</mat-label>
          <input matInput type="number" formControlName="lowStockThreshold" [disabled]="!canEditLowStockConfig" min="0" />
          <mat-hint>Alert when remaining units fall to or below this value.</mat-hint>
        </mat-form-field>

        <mat-slide-toggle color="primary" formControlName="lowStockAlertsEnabled" [disabled]="!canEditLowStockConfig">
          Send low-stock alerts
        </mat-slide-toggle>
      </form>
    </div>

    <div mat-dialog-actions align="end">
      <button mat-button (click)="close()">Cancel</button>
      <button mat-raised-button color="primary" (click)="save()" [disabled]="form.invalid || saving">
        <mat-icon>save</mat-icon>
        Save
      </button>
    </div>
  `,
})
export class SupplyStockUpdateDialogComponent {
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private fb = inject(FormBuilder);
  private errorService = inject(ErrorService);
  private dialogRef = inject(MatDialogRef<SupplyStockUpdateDialogComponent>);
  private data = inject(MAT_DIALOG_DATA) as DialogData;
  private authService = inject(AuthService);
  private stateService = inject(StateService);

  saving = false;

  private readonly extUrls = {
    totalRemaining: `${this.backendUrl}/StructureDefinition/totalRemaining`,
    packItemRemaining: `${this.backendUrl}/StructureDefinition/packItemRemaining`,
    modifiedHistory: `${this.backendUrl}/StructureDefinition/modifiedHistory`,
    totalRemainingChanges: `${this.backendUrl}/StructureDefinition/totalRemainingChanges`,
    lowStockThreshold: `${this.backendUrl}/StructureDefinition/lowStockThreshold`,
    lowStockAlertsEnabled: `${this.backendUrl}/StructureDefinition/lowStockAlertsEnabled`,
  } as const;

  substanceName =
    this.data.resource?.suppliedItem?.itemCodeableConcept?.text ||
    this.data.resource?.suppliedItem?.itemCodeableConcept?.coding?.[0]?.display ||
    this.data.resource?.suppliedItem?.itemCodeableConcept?.coding?.[0]?.code ||
    this.data.resource?.suppliedItem?.itemReference?.display ||
    this.data.resource?.id ||
    'Supply item';

  unitLabel = this.extractDispensableUnit(this.data.resource);

  private readonly initialQuantityRemaining = this.extractTotalRemaining(this.data.resource);
  private readonly initialLowStockThreshold = this.extractLowStockThreshold(this.data.resource);
  private readonly initialLowStockAlertsEnabled = this.extractLowStockAlertsEnabled(this.data.resource);
  readonly canEditLowStockConfig = this.authService.can('supplyDelivery' as any, 'editStock');

  form = this.fb.group({
    quantityRemaining: [this.extractTotalRemaining(this.data.resource), [Validators.min(0)]],
    packagesRemaining: [this.extractPackagesRemaining(this.data.resource), [Validators.min(0)]],
    lowStockThreshold: [{ value: this.initialLowStockThreshold ?? null, disabled: !this.canEditLowStockConfig }, [Validators.min(0)]],
    lowStockAlertsEnabled: [{ value: this.initialLowStockAlertsEnabled ?? false, disabled: !this.canEditLowStockConfig }],
  });

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    const id = (this.data.resource?.id ?? '').toString().trim();
    if (!id) {
      this.errorService.openandCloseError('Cannot update: missing SupplyDelivery id');
      return;
    }

    const formValue = this.form.getRawValue();
    const qty = Number(formValue.quantityRemaining);
    const packages = Number(formValue.packagesRemaining);

    const next = JSON.parse(JSON.stringify(this.data.resource)) as any;
    next.extension = Array.isArray(next.extension) ? next.extension : [];

    const beforeQty = this.initialQuantityRemaining === null ? undefined : this.initialQuantityRemaining;
    const afterQty = Number.isFinite(qty) ? qty : undefined;

    if (beforeQty !== afterQty) {
      const changedAt = new Date().toISOString();
      this.appendModifiedHistory(next, {
        field: 'quantityRemaining',
        beforeValue: beforeQty,
        editedValue: afterQty,
        unit: this.unitLabel,
        changedAt,
      });
      this.appendTotalRemainingChangeEntry(next, {
        beforeValue: beforeQty,
        editedValue: afterQty,
        unit: this.unitLabel,
        changedAt,
      });
    }

    this.upsertTotalRemaining(next, afterQty, this.unitLabel);
    this.upsertPackagesRemaining(next, Number.isFinite(packages) ? packages : undefined);
    const normalizedThreshold = this.normalizeThreshold(formValue.lowStockThreshold);
    this.upsertLowStockThreshold(next, normalizedThreshold, this.unitLabel);
    const normalizedAlertsFlag = this.normalizeBoolean(formValue.lowStockAlertsEnabled);
    this.upsertLowStockAlertsEnabled(next, normalizedAlertsFlag);

    this.saving = true;

    this.http
      .put(`${this.backendUrl}/SupplyDelivery/${encodeURIComponent(id)}`, next, {
        headers: {
          'Content-Type': 'application/fhir+json',
          Accept: 'application/fhir+json',
          Prefer: 'return=representation',
        },
      })
      .pipe(take(1))
      .subscribe({
        next: (updated: any) => {
          this.saving = false;
          // Incrementally update the global state with the fresh resource
          this.stateService.processOrgWideBundleTransaction({
            resourceType: 'Bundle',
            type: 'collection',
            entry: [{ resource: updated }]
          } as any);
          this.dialogRef.close(updated as SupplyDelivery);
        },
        error: (err) => {
          console.error('Failed to update SupplyDelivery', err);
          this.saving = false;
          this.errorService.openandCloseError('Failed to update stock. Please try again.');
        },
      });
  }

  private extractTotalRemaining(resource: any): number | null {
    const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.totalRemaining);
    const v = ext?.valueQuantity?.value ?? ext?.valueInteger ?? null;
    const n = v === null || v === undefined ? NaN : Number(v);
    return Number.isFinite(n) ? n : null;
  }

  private extractPackagesRemaining(resource: any): number | null {
    const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.packItemRemaining);
    const v = ext?.valueInteger ?? ext?.valueQuantity?.value ?? null;
    const n = v === null || v === undefined ? NaN : Number(v);
    return Number.isFinite(n) ? n : null;
  }

  private extractDispensableUnit(resource: any): string | undefined {
    // const originalDispensableUnit = (resource as SupplyDelivery).suppliedItem?.quantity?.unit
    const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.totalRemaining);
    const unit = (resource?.suppliedItem?.quantity as any)?.unit ?? ext?.valueQuantity?.unit
    const trimmed = String(unit ?? '').trim();
    return trimmed || undefined;
  }

  private extractLowStockThreshold(resource: any): number | null {
    const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.lowStockThreshold);
    const raw = ext?.valueQuantity?.value ?? ext?.valueInteger ?? null;
    const numeric = raw === null || raw === undefined ? NaN : Number(raw);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private extractLowStockAlertsEnabled(resource: any): boolean | null {
    const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.lowStockAlertsEnabled);
    if (ext?.valueBoolean === true || ext?.valueBoolean === false) {
      return ext.valueBoolean;
    }
    return null;
  }

  private upsertTotalRemaining(resource: any, value?: number, unit?: string) {
    const idx = (resource.extension as any[]).findIndex((e: any) => e?.url === this.extUrls.totalRemaining);
    const payload: any = {
      url: this.extUrls.totalRemaining,
      valueQuantity: {
        ...(value !== undefined ? { value } : {}),
        ...(unit ? { unit } : {}),
      },
    };

    if (idx >= 0) {
      resource.extension[idx] = payload;
    } else {
      resource.extension.push(payload);
    }
  }

  private upsertPackagesRemaining(resource: any, value?: number) {
    const idx = (resource.extension as any[]).findIndex((e: any) => e?.url === this.extUrls.packItemRemaining);
    const payload: any = {
      url: this.extUrls.packItemRemaining,
      valueInteger: value ?? 0,
    };

    if (idx >= 0) {
      resource.extension[idx] = payload;
    } else {
      resource.extension.push(payload);
    }
  }

  private upsertLowStockThreshold(resource: any, value: number | null, unit?: string) {
    resource.extension = Array.isArray(resource.extension) ? resource.extension : [];
    const extensions = resource.extension as any[];
    const idx = extensions.findIndex((ext: any) => ext?.url === this.extUrls.lowStockThreshold);

    if (value === null) {
      if (idx >= 0) {
        extensions.splice(idx, 1);
      }
      return;
    }

    const payload: any = {
      url: this.extUrls.lowStockThreshold,
      valueQuantity: {
        value,
        ...(unit ? { unit } : {}),
      },
    };

    if (idx >= 0) {
      extensions[idx] = payload;
    } else {
      extensions.push(payload);
    }
  }

  private upsertLowStockAlertsEnabled(resource: any, value: boolean | null) {
    resource.extension = Array.isArray(resource.extension) ? resource.extension : [];
    const extensions = resource.extension as any[];
    const idx = extensions.findIndex((ext: any) => ext?.url === this.extUrls.lowStockAlertsEnabled);

    if (value === null) {
      if (idx >= 0) {
        extensions.splice(idx, 1);
      }
      return;
    }

    const payload: any = {
      url: this.extUrls.lowStockAlertsEnabled,
      valueBoolean: value,
    };

    if (idx >= 0) {
      extensions[idx] = payload;
    } else {
      extensions.push(payload);
    }
  }

  private appendModifiedHistory(
    resource: any,
    entry: {
      field: string;
      beforeValue?: number;
      editedValue?: number;
      unit?: string;
      changedAt: string;
    }
  ) {
    resource.extension = Array.isArray(resource.extension) ? resource.extension : [];

    resource.extension.push({
      url: this.extUrls.modifiedHistory,
      extension: [
        { url: 'field', valueString: entry.field },
        ...(entry.beforeValue !== undefined
          ? [
            {
              url: 'beforeValue',
              valueQuantity: {
                value: entry.beforeValue,
                ...(entry.unit ? { unit: entry.unit } : {}),
              },
            },
          ]
          : []),
        ...(entry.editedValue !== undefined
          ? [
            {
              url: 'editedValue',
              valueQuantity: {
                value: entry.editedValue,
                ...(entry.unit ? { unit: entry.unit } : {}),
              },
            },
          ]
          : []),
        { url: 'changedAt', valueDateTime: entry.changedAt },
      ],
    });
  }

  private appendTotalRemainingChangeEntry(
    resource: any,
    entry: {
      beforeValue?: number;
      editedValue?: number;
      unit?: string;
      changedAt: string;
    }
  ) {
    if (entry.beforeValue === undefined && entry.editedValue === undefined) return;

    resource.extension = Array.isArray(resource.extension) ? resource.extension : [];

    const currentUser = this.authService.user.getValue();
    const practitionerReference = currentUser?.fhirResourceId
      ? {
        reference: `Practitioner/${currentUser.fhirResourceId}`,
        display: currentUser?.display || currentUser?.name || currentUser?.email,
      }
      : undefined;
    const practitionerDisplay = currentUser?.display || currentUser?.name || currentUser?.email;

    const children: any[] = [
      { url: 'dateChanged', valueDateTime: entry.changedAt },
      practitionerReference
        ? { url: 'practitionerWhoChanged', valueReference: practitionerReference }
        : practitionerDisplay
          ? { url: 'practitionerWhoChanged', valueString: practitionerDisplay }
          : null,
      ...(entry.beforeValue !== undefined
        ? [{
          url: 'totalRemainingBeforeChange',
          valueQuantity: {
            value: entry.beforeValue,
            ...(entry.unit ? { unit: entry.unit } : {}),
          },
        }]
        : []),
      ...(entry.editedValue !== undefined
        ? [{
          url: 'totalRemainingAfterChange',
          valueQuantity: {
            value: entry.editedValue,
            ...(entry.unit ? { unit: entry.unit } : {}),
          },
        }]
        : []),
    ].filter(Boolean);

    resource.extension.push({
      url: this.extUrls.totalRemainingChanges,
      extension: children,
    });
  }

  private normalizeThreshold(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) {
      return null;
    }
    return numeric;
  }

  private normalizeBoolean(value: any): boolean | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (!normalized) return null;
      if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
      if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    }
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
    }
    return null;
  }
}
